import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react'
import { RequestContext } from '../../../context/RequestContext'
import { LocationContext } from '../../../context/LocationContext'
import { reqAddressLatLng, getSgAddressByPostalCode } from '../../../service/location'
import { motion, AnimatePresence } from 'framer-motion'
import { ModernInput, PriceCard } from '../../commons/BookingFormComponents'
import { PtAnalyzingLoader } from './PtAnalyzingLoader'
import { computePackagePrice } from '../../../utils/pt-package-price'
import { MarketAddressFields } from '../../commons/MarketAddressFields'
import { getMarketByName, hasFeature } from '../../../utils/markets'
import { formatMoney } from '../../../utils/format-money'
import {
    ChevronLeft,
    ChevronRight,
    MapPin,
    AlertTriangle,
    Target,
    Calendar,
    Dumbbell,
    TrendingUp,
    Sparkles,
    Home
} from 'lucide-react'

// Timeline recommendations (reused from TrainerMatch)
const getTimelineRecommendation = (fitnessGoal, bodyWeight) => {
    const goal = (fitnessGoal || "").trim()

    const recommendations = {
        "Lose Fat and Build Muscle": {
            weeks: { min: 12, max: 16 },
            sessionsPerWeek: "3",
            description: "Body recomposition requires consistent training to burn fat while preserving and building lean muscle."
        },
        "Lose Weight": {
            weeks: { min: 8, max: 12 },
            sessionsPerWeek: "2-3",
            description: "A structured program combining cardio and strength training for sustainable weight loss."
        },
        "Build Muscle": {
            weeks: { min: 12, max: 16 },
            sessionsPerWeek: "3-4",
            description: "Progressive overload training to maximize muscle growth and strength development."
        },
        "Prenatal / Postnatal": {
            weeks: { min: 8, max: 12 },
            sessionsPerWeek: "1-2",
            description: "Safe, gentle exercises tailored for pre/postnatal fitness with focus on core recovery & overall wellness."
        }
    }

    let rec = recommendations[goal]
    if (!rec) {
        const goalLower = goal.toLowerCase()
        if (goalLower.includes("lose") && goalLower.includes("muscle")) {
            rec = recommendations["Lose Fat and Build Muscle"]
        } else if (goalLower.includes("lose") || goalLower.includes("weight")) {
            rec = recommendations["Lose Weight"]
        } else if (goalLower.includes("muscle") || goalLower.includes("build")) {
            rec = recommendations["Build Muscle"]
        } else if (goalLower.includes("prenatal") || goalLower.includes("postnatal")) {
            rec = recommendations["Prenatal / Postnatal"]
        } else {
            rec = {
                weeks: { min: 10, max: 14 },
                sessionsPerWeek: "2-3",
                description: "A personalised fitness program designed to help you reach your goals with consistent training."
            }
        }
    }

    if (bodyWeight) {
        const heavierWeights = [">100kg", "91-100", "81-90"]
        if (heavierWeights.includes(bodyWeight)) {
            const sessionMap = { "1-2": "2-3", "2-3": "3-4", "3": "3-4", "3-4": "3-4" }
            rec = {
                ...rec,
                weeks: { min: rec.weeks.min + 2, max: rec.weeks.max + 4 },
                sessionsPerWeek: sessionMap[rec.sessionsPerWeek] || rec.sessionsPerWeek
            }
        }
    }

    return rec
}

// Helper to extract criteria values
const getCriteriaValue = (requestCriteria, questionCode) => {
    if (!requestCriteria || !Array.isArray(requestCriteria)) return null
    for (let i = 0; i < requestCriteria.length; i++) {
        const item = requestCriteria[i]
        if (item && item[questionCode] !== undefined && item[questionCode] !== null) {
            return item[questionCode]
        }
    }
    return null
}

// Animation variants
const containerVariants = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: { staggerChildren: 0.06, delayChildren: 0.05 }
    }
}

const itemVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.25 } }
}

export const PtPlanAndPostalCode = ({ goToSection2, goToSection5, goBack }) => {
    const [phase, setPhase] = useState("analyzing") // "analyzing" | "planAndPostal"
    const [postalCode, setPostalCode] = useState("")
    const [blkStreet, setBlkStreet] = useState("")
    const [errorMessage, setErrorMessage] = useState("")
    const [isAddressValid, setIsAddressValid] = useState(false)
    const [isValidating, setIsValidating] = useState(false)

    const {
        requestCriteria, requestDescription, setRequestDescription,
        setUserLocation, requestType, businessType,
        filesToUpload, filesToAttach, setFilesToAttach,
        selectedPackage, serviceDate, startTime,
        serviceCost, setServiceCost,
        originalCost, setOriginalCost,
        totalDiscountPercent
    } = useContext(RequestContext)

    const { countryName, countryCd, countryCurrCd, setUserLatitude, setUserLongitude, location } = useContext(LocationContext)

    // Singapore: 6-digit postal code + OneMap. UAE / Malaysia: MarketAddressFields.
    const market = getMarketByName(countryName)
    const isSgAddress = !market || market.addressMode === 'sg-postal-onemap'
    // Countries without the trainer match step (utils/markets.js features)
    // do not promise an instant match here; our team matches after sign-in.
    const trainerMatch = hasFeature(market, 'trainerMatch')
    const [marketAddress, setMarketAddress] = useState({ complete: false, userLocation: '' })
    const priceCd = market?.countryCd ?? 'sg'
    // SG keeps its existing "$89" rendering; other markets use their currency.
    const money = (v) => (priceCd === 'sg' ? `$${v}` : formatMoney(v, priceCd, 0))
    const canContinue = isSgAddress ? !!postalCode : marketAddress.complete

    // Online-booking discount only applies on this step (PtPlanAndPostalCode)
    const packagePrice = useMemo(
        () => (selectedPackage
            ? computePackagePrice(selectedPackage, serviceDate, startTime, requestCriteria, { applyOnlineDiscount: true, countryCd: priceCd })
            : null),
        [selectedPackage, serviceDate, startTime, requestCriteria, priceCd]
    )

    useEffect(() => {
        if (!selectedPackage) return
        const { totalCost, originalTotalCost } = computePackagePrice(
            selectedPackage, serviceDate, startTime, requestCriteria, { applyOnlineDiscount: true, countryCd: priceCd }
        )
        setServiceCost(totalCost)
        setOriginalCost(originalTotalCost)
    }, [selectedPackage, serviceDate, startTime, requestCriteria, setServiceCost, setOriginalCost, priceCd])


    // Extract fitness criteria
    const fitnessGoal = useMemo(() => getCriteriaValue(requestCriteria, "1"), [requestCriteria])
    const bodyWeight = useMemo(() => getCriteriaValue(requestCriteria, "2"), [requestCriteria])
    const trainingLocation = useMemo(() => getCriteriaValue(requestCriteria, "5"), [requestCriteria])

    const timeline = useMemo(() => getTimelineRecommendation(fitnessGoal, bodyWeight), [fitnessGoal, bodyWeight])

    // Auto-build request description from criteria
    useEffect(() => {
        if (!location || requestDescription) return
        setUserLocation(location)
        // When a package is selected, keep the description set by selectPackageAndBook
        if (selectedPackage) return
        if (requestType === "Fitness Trainer") {
            let remarks = ""
            for (let i = 0; i < requestCriteria.length; i++) {
                if (requestCriteria[i][5] != undefined) {
                    if (requestCriteria[i][5].indexOf('(') > 0) {
                        remarks = "Personal training at " + requestCriteria[i][5].substr(0, requestCriteria[i][5].indexOf('(')) + ". "
                    } else {
                        remarks = "Personal training at " + requestCriteria[i][5] + ". "
                    }
                }
                if (requestCriteria[i][7] != undefined) {
                    remarks = remarks + requestCriteria[i][7] + ". "
                }
            }
            setRequestDescription(remarks)
        }
    }, [location, setUserLocation, requestCriteria, requestType, setRequestDescription, requestDescription, selectedPackage])

    const onAnalyzingComplete = useCallback(() => {
        setPhase("planAndPostal")
    }, [])

    // Dismiss keyboard when address is validated
    useEffect(() => {
        if (isAddressValid) {
            if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                document.activeElement.blur()
            }
        }
    }, [isAddressValid])

    const validatePostalCode = async (postalCode) => {
        setIsValidating(true)
        setErrorMessage("")
        setBlkStreet("")

        if (countryCd === "sg") {
            try {
                const result = await getSgAddressByPostalCode(postalCode)
                setUserLatitude(result.latitude)
                setUserLongitude(result.longitude)
                setErrorMessage(result.address)
                setBlkStreet(result.blkStreet || result.address)
                setIsAddressValid(true)
            } catch (error) {
                setErrorMessage(error.message || "Address is incorrect")
                setBlkStreet("")
                setIsAddressValid(false)
            }
        } else {
            await reqAddressLatLng(countryName + " " + postalCode)
                .then(async (result) => {
                    setUserLatitude(result.latitude)
                    setUserLongitude(result.longitude)
                    setErrorMessage(result.address)
                    setBlkStreet(result.address)
                    setIsAddressValid(true)
                }, (errorCode) => {
                    setErrorMessage(errorCode)
                    setBlkStreet("")
                    setIsAddressValid(false)
                })
        }
        setIsValidating(false)
    }

    const updatePostalCode = (e) => {
        const re = /^[0-9\b]+$/
        if (e.target.value === "" || re.test(e.target.value)) {
            setPostalCode(e.target.value)
            setErrorMessage("")
            setBlkStreet("")
            setIsAddressValid(false)
            e.target.setCustomValidity("")
            if (countryCd === "sg") {
                if (e.target.value.length === 6) {
                    validatePostalCode(e.target.value)
                }
            }
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        try {
            if (typeof gtag != "undefined") {
                if (requestType === "Fitness Trainer") {
                    gtag('event', 'conversion', { 'send_to': 'AW-823304067/nvEnCMG8i-UCEIO_yogD' })
                }
            }
        } catch (error) {
            console.log(error)
        }

        if (!isSgAddress) {
            setUserLocation(marketAddress.userLocation)
            goToSection5()
            return
        }

        let fullAddress = blkStreet
        if (fullAddress) {
            setUserLocation(`Singapore ${postalCode}, ${fullAddress}`)
        } else {
            setUserLocation(`Singapore ${postalCode}`)
        }

        goToSection5()
    }

    // ============ ANALYZING PHASE ============
    if (phase === "analyzing") {
        return <PtAnalyzingLoader onComplete={onAnalyzingComplete} />
    }

    // ============ PLAN + POSTAL CODE PHASE ============
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-134"
        >
            <form className="max-w-lg mx-auto py-3" onSubmit={handleSubmit}>
                <motion.div
                    className="space-y-4"
                    variants={containerVariants}
                    initial="initial"
                    animate="animate"
                >
                    {/* Header */}
                    <motion.div variants={itemVariants} className="text-center">
                        <div className='flex gap-2 items-center justify-center'>
                            <h3 className="text-lg font-bold">
                                Your Personalised Fitness Plan
                            </h3>
                        </div>
                        <p className="text-xs text-gray-500">
                            {selectedPackage ? "Review your selection below" : "Based on your goals and preferences"}
                        </p>
                    </motion.div>

                    {selectedPackage && packagePrice ? (
                        /* Package summary + price (dark card to match consultation card) */
                        <motion.div
                            variants={itemVariants}
                            className="bg-slate-900 backdrop-blur-md rounded-2xl border border-white/10 pt-4 px-3"
                        >
                            <p className="text-sm mb-3 text-white/70">
                                {timeline.description}
                            </p>

                            {/* Combined price + sessions/duration pills */}
                            <div className="bg-white/5 rounded-xl py-2 border border-white/10 text-center">
                                {/* Price */}
                                <div className="flex items-baseline justify-center gap-1">
                                    <span className="text-2xl md:text-3xl font-extrabold text-white">{money(packagePrice.perSessionPrice)}</span>
                                    <span className="text-sm text-white/60">/session</span>
                                </div>

                                {/* Strikethrough original price + per-session savings */}
                                {/* {packagePrice.onlineDiscountPerSession > 0 && (
                                    <div className="mt-1 text-xs text-white/60">
                                        <span className="line-through">{money(packagePrice.grossPerSessionPrice)}/session</span>
                                        <span className="ml-1 text-green-300">
                                            (−{money(packagePrice.onlineDiscountPerSession)} online)
                                        </span>
                                    </div>
                                )} */}

                                {/* Sessions + Duration pills inline with price */}
                                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 text-white rounded-full text-xs font-medium border border-white/15">
                                        <Dumbbell className="w-3.5 h-3.5 text-orange-300" />
                                        {selectedPackage.totalServices} Sessions
                                    </span>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 text-white rounded-full text-xs font-medium border border-white/15">
                                        <Calendar className="w-3.5 h-3.5 text-orange-300" />
                                        {selectedPackage.packageDuration} {selectedPackage.packageDurationUnit}s
                                    </span>
                                </div>

                                {/* Total + Savings line */}
                                <div className="mt-3 pt-3 border-t border-white/10">
                                    <div className="flex items-baseline justify-center gap-2">
                                        <span className="text-xs text-white/50 uppercase tracking-wider">Total</span>
                                        <span className="text-lg font-bold text-white">{money(packagePrice.totalCost)}</span>
                                        {packagePrice.totalSavings > 0 && (
                                            <span className="text-xs text-white/40 line-through">{money(packagePrice.originalTotalCost)}</span>
                                        )}
                                    </div>
                                    {packagePrice.totalSavings > 0 && (
                                        <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 text-xs font-semibold">
                                            <Sparkles className="w-3 h-3" />
                                            Exclusive Online Booking Discount
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ) : (
                        /* Shortened Plan Card (consultation flow) */
                        <motion.div
                            variants={itemVariants}
                            className="bg-slate-900 backdrop-blur-md rounded-2xl border border-white/10 py-4 px-3"
                        >
                            <p className="text-sm mb-3 text-white/70">{timeline.description}</p>

                            <div className="grid grid-cols-2 gap-2 mb-3">
                                <div className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                                    <div className="inline-flex items-center gap-1 mx-auto justify-center">
                                        <Calendar className="w-4 h-4 text-orange-400" />
                                        <div className="text-lg font-bold text-white">{timeline.weeks.min}-{timeline.weeks.max}</div>
                                    </div>
                                    <div className="text-xs text-white/60">Weeks</div>
                                </div>
                                <div className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                                    <div className="inline-flex items-center gap-1">
                                        <Dumbbell className="w-4 h-4 text-orange-400" />
                                        <div className="text-lg font-bold text-white">{timeline.sessionsPerWeek}</div>
                                    </div>
                                    <div className="text-xs text-white/60">Sessions / Week</div>
                                </div>
                            </div>

                            <div className="flex justify-center">
                                {fitnessGoal && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-500 text-orange-100 rounded-full text-xs font-medium">
                                        <Target className="w-3 h-3" /> {fitnessGoal}
                                    </span>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* Lure / CTA for Postal Code */}
                    <motion.div variants={itemVariants} className="text-center pt-1">
                        <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-50 border border-orange-200 rounded-full mb-3">
                            <Sparkles className="w-4 h-4 text-orange-500" />
                            <span className="text-sm font-semibold text-orange-700">{trainerMatch ? <>Now let&apos;s find the best trainer near you!</> : 'Almost there - where should your trainer meet you?'}</span>
                        </div>
                        <p className="text-xs text-gray-500">{isSgAddress ? 'Enter your postal code so we can match our nearest trainer' : 'Enter your address so we can match a trainer near you'}</p>
                    </motion.div>

                    {isSgAddress ? (
                    <>
                    {/* Postal Code Input - pulses until complete instead of autofocus,
                        which opens the phone keyboard over the plan cards */}
                    <motion.div variants={itemVariants}>
                        <ModernInput
                            type="text"
                            placeholder="Enter postal code"
                            value={postalCode}
                            onChange={updatePostalCode}
                            icon={MapPin}
                            minLength={6}
                            maxLength={6}
                            required
                            highlight={postalCode.length < 6}
                            centerText
                            className="max-w-xs mx-auto"
                        />
                    </motion.div>

                    {/* Address validation feedback */}
                    <div className="text-center flex items-center justify-center">
                        <AnimatePresence mode="wait">
                            {errorMessage && !isAddressValid && postalCode.length === 6 && (
                                <motion.div
                                    key="error"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                >
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-600 rounded-full text-sm">
                                        <AlertTriangle className="w-4 h-4" />
                                        {errorMessage}. Please enter address manually.
                                    </div>
                                </motion.div>
                            )}

                            {isAddressValid && blkStreet && (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                >
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-700 rounded-full text-sm">
                                        <MapPin className="w-4 h-4" />
                                        {blkStreet}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Manual address entry if postal code lookup failed */}
                    {errorMessage && !isAddressValid && postalCode.length === 6 && (
                        <motion.div variants={itemVariants} className="space-y-3">
                            <ModernInput
                                type="text"
                                placeholder="e.g. 123 Orchard Road"
                                value={blkStreet}
                                onChange={(e) => setBlkStreet(e.target.value)}
                                icon={Home}
                                label="Block & Street"
                                required
                            />
                        </motion.div>
                    )}
                    </>
                    ) : (
                        <motion.div variants={itemVariants}>
                            <MarketAddressFields market={market} onChange={setMarketAddress} highlight />
                        </motion.div>
                    )}
                </motion.div>

                {/* Navigation Buttons */}
                <motion.div
                    className="flex justify-between items-center mt-4 pt-2 px-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    {/* Back Button */}
                    <motion.button
                        type="button"
                        onClick={() => {
                            // Step back (keeps the plan picked) when the form
                            // passes goBack; older callers start over
                            if (goBack) {
                                goBack()
                                return
                            }
                            setFilesToAttach([])
                            filesToUpload.length = 0
                            goToSection2()
                        }}
                        className="flex items-center gap-2 px-4 py-3 text-gray-500 hover:text-orange-600 rounded-xl hover:bg-orange-50 transition-all duration-300"
                        whileHover={{ x: -3 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="font-medium hidden sm:inline">Back</span>
                    </motion.button>

                    {/* Next Button */}
                    <motion.button
                        type="submit"
                        disabled={!canContinue}
                        className={`
                            flex items-center gap-2 px-8 py-4 
                            font-semibold text-xl
                            rounded-full shadow-lg
                            transition-all duration-300
                            ${canContinue
                                ? 'bg-linear-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-500 text-white shadow-orange-200/50'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                            }
                        `}
                        whileHover={canContinue ? { scale: 1.02, x: 3 } : {}}
                        whileTap={canContinue ? { scale: 0.98 } : {}}
                    >
                        <span>{trainerMatch ? 'Find Trainer' : 'Continue'}</span>
                        <ChevronRight className="w-5 h-5" />
                    </motion.button>
                </motion.div>
            </form>
        </motion.div>
    )
}
